import React, { FunctionComponent, useMemo } from "react";
import { IconProps } from "./IconProps";
import { useGetIconQuery } from "@store/services/icon";

export type ExternalIcons =
  | "play"
  | "pause"
  | "facebook"
  | "instagram-gradient"
  | "instagram"
  | "link"
  | "mail"
  | "sms"
  | "twitter"
  | "x"
  | "close"
  | "search"
  | "transcript"
  | "fullscreen"
  | "fullscreen-exit"
  | "subtitles"
  | "location"
  | "car"
  | "map"
  | "plus"
  | "minus"
  | "current-location"
  | "ai"
  | "calendar"
  | "arrow-full"
  | "arrow-right"
  | "arrow-left"
  | "menu"
  | "trash";

/**
 * Loads an SVG and allows for resizing and dynamic coloring.
 * SVG must reside in the `public/images/icons` folder and should make use of the `currentColor`
 * when setting color so that dynamic coloring works.
 *
 * This allows for icons be dynamic while not needing to include the SVGs as part of the
 * javascript bundle, keeping bundle size down.
 */
const ExternalIconBase: FunctionComponent<
  IconProps & { svgName: ExternalIcons }
> = ({ size = 24, color, testID, svgName }) => {
  const { data, isLoading } = useGetIconQuery({ svgName });

  const svgContent = useMemo(() => {
    if (!data) {
      return;
    }

    return data.svg
      .replace(/width="\d+"/, `width="${size}"`)
      .replace(/height="\d+"/, `height="${size}"`);
  }, [data, size]);

  if (isLoading || !svgContent) {
    return <span style={{ width: size, height: size }} />;
  }

  return (
    <span
      data-testid={testID}
      style={{ color, width: size, height: size, display: "flex" }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

const ExternalIcon = React.memo(ExternalIconBase);

export { ExternalIcon };
